import { Link, Outlet, useLocation } from 'react-router';
import { Coins, Plus, Sparkles, UserRound } from 'lucide-react';
import clsx from 'clsx';
import { LANGS, type Lang } from '@tangazo/shared';
import { useConfig, useFlags, useMe } from '@/api/hooks';
import { useWallet } from '@/api/v2';
import { setLang, useT } from '@/lib/i18n';
import { useEffect } from 'react';
import { storage } from '@/lib/storage';
import { TabBar } from './TabBar';

export function initials(u: { name: string; phone: string }) {
  const n = u.name.trim();
  return n ? n.split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase() : u.phone.slice(-2);
}

export function LangSwitch() {
  const { t, tIn, lang } = useT();
  return (
    <div role="group" aria-label={t('lang.label')} className="flex gap-0.5 rounded-full border border-line bg-white p-[3px]">
      {LANGS.map((l: Lang) => (
        <button
          key={l}
          type="button"
          lang={l}
          aria-pressed={l === lang}
          onClick={() => setLang(l)}
          className={clsx('cursor-pointer rounded-full px-2 py-1 text-[12px] sm:px-3', l === lang ? 'bg-ink text-white' : 'text-muted')}
        >
          <span className="sm:hidden">{l.toUpperCase()}</span><span className="hidden sm:inline">{tIn(l, 'lang.name')}</span>
        </button>
      ))}
    </div>
  );
}

// "Salio: tokeni 40" in the header, linking to the wallet.
function BalanceChip() {
  const { t } = useT();
  const w = useWallet();
  if (!w.data) return null;
  return (
    <Link to="/wallet" title={t('v2.balance', { n: w.data.balance })} className="inline-flex min-h-[34px] items-center gap-1 rounded-full border border-line bg-white px-2.5 text-[13px] font-bold tabular-nums">
      <Coins className="size-4 text-brand" aria-hidden />{w.data.balance}
    </Link>
  );
}

export default function Layout() {
  const { t, lang } = useT();
  const me = useMe();
  const flags = useFlags();
  const config = useConfig();
  const { pathname } = useLocation();
  const wide = pathname === '/' || pathname === '/about' || pathname === '/templates' || pathname === '/create';
  const bare = pathname === '/create'; // the create page's composer covers the bottom
  // Bottom tabs on the browsing pages; pages with their own bottom button (template, order, job) go without.
  const tabs = ['/', '/templates', '/me', '/wallet'].includes(pathname);

  // One "visit" per browser tab, for the Stats funnel.
  useEffect(() => {
    let sid = storage.get('sid', sessionStorage);
    if (sid) return;
    sid = Math.random().toString(36).slice(2, 12);
    storage.set('sid', sid, sessionStorage);
    void fetch('/api/events', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type: 'visit', sid, lang }), keepalive: true }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = t('meta.title');
  }, [lang, t]);

  return (
    <>
      <header className="sticky top-0 z-10 border-b border-black/5 bg-canvas/85 backdrop-blur-md backdrop-saturate-150">
        <div className="wrap-wide flex items-center justify-between gap-2 py-2">
          <Link to="/" className="flex items-center" aria-label="Tangazo">
            <img src="/icons/logo.png" alt="Tangazo" width={131} height={26} className="h-[26px] w-auto" />
          </Link>
          <div className="flex items-center gap-2">
            {!bare && (
              <Link to="/create" className="hidden items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold hover:bg-black/5 sm:inline-flex">
                <Plus className="size-4 text-brand" aria-hidden />{t('nav.create')}
              </Link>
            )}
            <Link to="/templates" className="hidden items-center gap-1.5 rounded-full px-3 py-2 text-sm font-semibold hover:bg-black/5 sm:inline-flex">
              <Sparkles className="size-4 text-brand" aria-hidden />{t('nav.templates')}
            </Link>
            <LangSwitch />
            {me.data && flags.wallet_v2 && <BalanceChip />}
            {me.data ? (
              <Link to="/me" aria-label={t('acct.account')} className="grid size-[34px] place-items-center rounded-full bg-brand text-sm font-bold text-white">
                {initials(me.data)}
              </Link>
            ) : (
              <Link to="/login" className="inline-flex min-h-[34px] items-center gap-1.5 rounded-full bg-ink px-3 text-sm font-semibold text-white sm:px-3.5">
                <UserRound className="size-4" aria-hidden />
                <span className="hidden sm:inline">{t('acct.signin')}</span>
              </Link>
            )}
          </div>
        </div>
      </header>
      <main className={clsx(wide ? 'wrap-wide' : 'wrap', tabs && 'pb-16 sm:pb-0')}>
        <Outlet />
      </main>
      {tabs && <TabBar />}
      {!bare && <footer className={clsx(wide ? 'wrap-wide' : 'wrap', 'py-10 text-center text-sm text-muted', tabs && 'hidden sm:block')}>
        {config.data?.supportWhatsapp && (
          <>
            <a className="text-brand-dark" href={`https://wa.me/${config.data.supportWhatsapp}?text=${encodeURIComponent(t('footer.help_message', { code: '' }))}`} target="_blank" rel="noopener">
              {t('footer.help')}
            </a>
            {' · '}
          </>
        )}
        <span>{t('footer.payments')}</span>
      </footer>}
    </>
  );
}
